import React, { useCallback, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faUser, faUsers } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import { GetUserData, PermissionGroupSummaryDto, PrincipalType } from 'apiClient/Api';

// Shares sind serverseitig untypisiert (any) – hier defensiv nachgebildet.
interface ProjectShareDto {
    id?: string;
    principalType?: PrincipalType | string;
    principalId?: string;
    principalName?: string | null;
}

export const ShareProjectDialog: React.FC<{
    projectId: string | null;
    projectName?: string;
    onClose: () => void;
}> = ({ projectId, projectName, onClose }) => {
    const { api, apiError } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const [shares, setShares] = useState<ProjectShareDto[]>([]);
    const [users, setUsers] = useState<GetUserData[]>([]);
    const [groups, setGroups] = useState<PermissionGroupSummaryDto[]>([]);
    const [mode, setMode] = useState<PrincipalType>(PrincipalType.User);
    const [pickUser, setPickUser] = useState<GetUserData | null>(null);
    const [pickGroup, setPickGroup] = useState<PermissionGroupSummaryDto | null>(null);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(() => {
        if (!projectId) return;
        setLoading(true);
        api.calculator
            .projectSharingList(projectId)
            .then((r) => setShares((r.data as unknown as ProjectShareDto[]) ?? []))
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, projectId, apiError]);

    useEffect(() => {
        if (!projectId) return;
        reload();
        // Benutzer + Gruppen für die Auswahl (Gruppen nur, wenn zugänglich).
        api.user.userListUsers().then((r) => setUsers(r.data ?? [])).catch(() => setUsers([]));
        api.permissions.permissionAdminListGroups().then((r) => setGroups(r.data ?? [])).catch(() => setGroups([]));
    }, [projectId, reload, api]);

    const add = async () => {
        if (!projectId) return;
        const principalId = mode === PrincipalType.User ? pickUser?.id : pickGroup?.id;
        if (!principalId) return;
        try {
            await api.calculator.projectSharingAdd(projectId, { principalType: mode, principalId });
            setPickUser(null);
            setPickGroup(null);
            enqueueSnackbar('Freigabe hinzugefügt.', { variant: 'success' });
            reload();
        } catch (e) {
            apiError(e);
        }
    };

    const remove = async (shareId: string) => {
        if (!projectId) return;
        try {
            await api.calculator.projectSharingRemove(projectId, shareId);
            reload();
        } catch (e) {
            apiError(e);
        }
    };

    const userLabel = (u: GetUserData) => `${u.name || ''} ${u.eMail ? `<${u.eMail}>` : ''}`.trim();
    const sharedIds = new Set(shares.map((s) => s.principalId));

    return (
        <Dialog open={!!projectId} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Projekt teilen{projectName ? `: ${projectName}` : ''}</DialogTitle>
            <DialogContent dividers>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={mode}
                    onChange={(_, v) => v && setMode(v)}
                    sx={{ mb: 2 }}
                >
                    <ToggleButton value={PrincipalType.User}>
                        <FontAwesomeIcon icon={faUser} style={{ marginRight: 6 }} /> Person
                    </ToggleButton>
                    <ToggleButton value={PrincipalType.Group}>
                        <FontAwesomeIcon icon={faUsers} style={{ marginRight: 6 }} /> Gruppe
                    </ToggleButton>
                </ToggleButtonGroup>

                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
                    {mode === PrincipalType.User ? (
                        <Autocomplete
                            size="small"
                            sx={{ flexGrow: 1 }}
                            options={users.filter((u) => !sharedIds.has(u.id))}
                            value={pickUser}
                            getOptionLabel={userLabel}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            onChange={(_, v) => setPickUser(v)}
                            renderInput={(params) => <TextField {...params} label="Person wählen" />}
                        />
                    ) : (
                        <Autocomplete
                            size="small"
                            sx={{ flexGrow: 1 }}
                            options={groups.filter((g) => !sharedIds.has(g.id))}
                            value={pickGroup}
                            getOptionLabel={(g) => g.name || ''}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            onChange={(_, v) => setPickGroup(v)}
                            renderInput={(params) => <TextField {...params} label="Gruppe wählen" />}
                            noOptionsText="Keine Gruppen verfügbar"
                        />
                    )}
                    <Button
                        variant="contained"
                        onClick={add}
                        disabled={mode === PrincipalType.User ? !pickUser : !pickGroup}
                    >
                        Teilen
                    </Button>
                </Stack>

                {loading && <LinearProgress sx={{ mb: 1 }} />}
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Aktuelle Freigaben
                </Typography>
                <List dense>
                    {shares.map((s) => (
                        <ListItem
                            key={s.id}
                            secondaryAction={
                                <IconButton size="small" color="error" onClick={() => s.id && remove(s.id)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </IconButton>
                            }
                        >
                            <Box sx={{ mr: 1, color: 'text.secondary' }}>
                                <FontAwesomeIcon icon={s.principalType === PrincipalType.Group ? faUsers : faUser} />
                            </Box>
                            <ListItemText
                                primary={s.principalName || s.principalId}
                                secondary={s.principalType === PrincipalType.Group ? 'Gruppe' : 'Person'}
                            />
                        </ListItem>
                    ))}
                    {shares.length === 0 && !loading && (
                        <Typography variant="body2" color="text.secondary">
                            Noch nicht geteilt.
                        </Typography>
                    )}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};
